"use client"

import { ColumnDef } from "@tanstack/react-table"
import { Check, MoreHorizontal } from "lucide-react"
import { Checkbox } from "@/Components/ui/checkbox"
import { Button } from "@/Components/ui/button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/Components/ui/dropdown-menu"
import { PageLock } from "@/Components/PageLockProvider"
import { format } from "date-fns"

export interface PageLockForTable extends PageLock {
    projectName: string; // Assuming you have a project name field
    pageName: string; // Assuming you have a page name field
}

export const columns = (HandleUnlockClicked:(id:number) => void): ColumnDef<PageLockForTable>[] => [
    {
        id: "select",
        header: ({ table }) => (
            <Checkbox
                checked={
                    table.getIsAllPageRowsSelected() ||
                    (table.getIsSomePageRowsSelected() && "indeterminate")
                }
                onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                aria-label="Select all"
            />
        ),
        cell: ({ row }) => (
            <div className="text-center">
            <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(value) => row.toggleSelected(!!value)}
                aria-label="Select row"
            />
            </div>
        ),
        enableSorting: false,
        enableHiding: false,
    },
    {
        id: "id",
        accessorKey: "id",
        header: "ID",
        cell: ({ row }) => {
            const id = row.getValue("id");
            return <div className="text-center">{ id as string }</div>
        },
    },
    {
        id: "userName",
        accessorKey: "userName",
        header: "User",
        cell: ({ row }) => {
            const userName = row.getValue("userName");
            return <div className="text-center">{ userName as string }</div>
        },
        size: 200,
    },
    {
        id: "projectName",
        accessorKey: "projectName",
        header: "Project",
        cell: ({ row }) => {
            const projectName = row.getValue("projectName");
            return <div className="text-center">{projectName as string}</div>
        },
        size: 200,
    },
    {
        id: "pageName",
        accessorKey: "pageName",
        header: "Page",
        cell: ({ row }) => {
            const page = row.getValue("pageName");
            return <div className="text-center">{page as string}</div>
        },
        size: 200,
    },
    {
        id: "lockTime",
        accessorKey: "lockTime",
        header: "Lock Time",
        meta: {
                filterVariant: 'date-range',
            },
        cell: ({ row }) => {
            const lockTime = row.getValue("lockTime");
            return <div className="text-center">{format(new Date(lockTime as string), "dd-MMM-yy HH:mm")}</div>
        },
        filterFn: (row, columnId, filterValue) => {
                    const value = new Date(row.getValue(columnId));
                    const from = filterValue.from ? new Date(filterValue.from) : null;
                    const to = filterValue.to ? new Date(filterValue.to) : null;
                    if (!from || !to) return true;
                    return value >= from && value <= to;
                },
        size: 200,
    },
    {
        id: "isLocked",
        accessorKey: "isLocked",
        header: "Locked",
        meta: {
                filterVariant: 'bool-select',
            },
        cell: ({ row }) => {
            const locked = row.getValue("isLocked");
            return <>{locked ? <Check className="m-auto" /> : <div className="text-center">-</div>}</>
        },
        filterFn: (row, columnId, filterValue) => {
                    // Custom filtering logic for the "accepted" column
                    if (filterValue === "true") {
                        return row.getValue(columnId) === true; // Show only true values
                    } else if (filterValue === "false") {
                        return row.getValue(columnId) === false; // Show only false values
                    } else {
                        return true; // Show all values if filterValue is not specified (e.g., reset/all)
                    }
                },
        size: 200,
    },
    {
        id: "actions",
        cell: ({ row }) => {

            return (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem
                             onClick={() => HandleUnlockClicked(Number.parseInt(row.getValue("id")))}
                        >
                            Unlock
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )
        },
    },
]

export const columnDisplayNames: [string, string][] = [
    ["id", "ID"],
    ["userName", "User"],
    ["projectName", "Project"],
    ["pageName", "Page"],
    ["lockTime", "Lock Time"],
    ["isLocked", "Locked"],
]
